import { z } from 'zod/v4'
import { WorkflowNodeBaseSchema } from '../../resources/types'
import { FilterSchema } from '../../../../../crm/features/pages/features/queries/resources/types'
import {
  StructuredExpressionSchema,
  StructuredFilterSchema,
  SortSchema,
  SortItemSchema,
} from '../../../../../crm/resources/filterTypes'

// -----------------------
// Shared Helper Schemas
// -----------------------

const ModelIdentifierBaseSchema = z.object({
  modelId: z.string().optional(),
  'model-id': z.string().optional(),
  modelHandle: z.string().optional(),
  'model-handle': z.string().optional(),
})

const requireModelIdentifier = <T extends z.ZodObject<any>>(schema: T) =>
  schema.refine(
    (value) =>
      !!value.modelId ||
      !!value['model-id'] ||
      !!value.modelHandle ||
      !!value['model-handle'],
    {
      message:
        'Either modelId/model-id or modelHandle/model-handle is required',
      path: ['modelId'],
    },
  )

const PayloadOrDataSchema = z
  .union([z.record(z.string(), z.unknown()), z.string()])
  .refine(
    (value) => {
      if (typeof value === 'string') {
        try {
          JSON.parse(value)
          return true
        } catch {
          return false
        }
      }
      return true
    },
    { message: 'Invalid JSON data' },
  )

// -----------------------
// instance.find
// -----------------------

const InstanceFindInputsSchema = z
  .object({
    id: z.string().optional().describe('The ID of the instance to fetch'),
    filters: FilterSchema.optional(),
    modelId: z.string().optional(),
    'model-id': z.string().optional(),
    modelHandle: z.string().optional(),
    'model-handle': z.string().optional(),
  })
  .refine((data) => data.id !== undefined || data.filters !== undefined, {
    message: 'Either "id" or "filters" must be provided',
  })
  .refine(
    (data) => {
      if (data.filters !== undefined) {
        return (
          data.modelId !== undefined ||
          data['model-id'] !== undefined ||
          data.modelHandle !== undefined ||
          data['model-handle'] !== undefined
        )
      }
      return true
    },
    {
      message:
        'When filters are provided, either modelId/model-id or modelHandle/model-handle is required',
    },
  )

// -----------------------
// instance.list
// -----------------------

const ExtendedSortSchema = z
  .union([SortSchema, z.record(z.string(), z.enum(['asc', 'desc']))])
  .optional()
  .transform((val) => {
    if (!val) return undefined

    if (SortItemSchema.safeParse(val).success) {
      return val
    }

    if (z.array(SortItemSchema).safeParse(val).success) {
      return val
    }

    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      const record = val as Record<string, 'asc' | 'desc'>
      const entries = Object.entries(record)
      if (entries.length > 0) {
        const transformed = entries.map(([key, direction]) => ({
          key,
          direction,
        }))
        return transformed.length === 1 ? transformed[0] : transformed
      }
    }

    return undefined
  })

const InstanceListInputsSchema = z
  .object({
    modelId: z
      .string()
      .optional()
      .describe('The ID of the model to list instances from'),
    'model-id': z
      .string()
      .optional()
      .describe('The ID of the model to list instances from'),
    modelHandle: z
      .string()
      .optional()
      .describe('The handle of the model to list instances from'),
    'model-handle': z
      .string()
      .optional()
      .describe('The handle of the model to list instances from'),
    filters: StructuredExpressionSchema.optional().describe(
      'Structured filter expression',
    ),
    matchField: z
      .string()
      .optional()
      .describe(
        'Primary CRM field handle to match on (upsert-style). Combined with matchFields/matchData.',
      ),
    matchFields: z
      .union([z.array(z.string()), z.string()])
      .optional()
      .describe(
        'Ordered CRM field handles for match fallback (e.g. glofox_id, phone, email)',
      ),
    matchData: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        'Values keyed by CRM field handle; blank values are skipped during ordered fallback',
      ),
    matchValue: z
      .unknown()
      .optional()
      .describe(
        'Shorthand value for matchField when matchData is omitted (single-field match)',
      ),
    sort: ExtendedSortSchema.describe(
      'Sort configuration (supports { key, direction } or { field: direction })',
    ),
    page: z
      .number()
      .optional()
      .default(1)
      .describe('Page number for pagination'),
    limit: z
      .number()
      .optional()
      .default(50)
      .describe('Number of items per page'),
    fields: z
      .array(z.string())
      .optional()
      .default([])
      .describe(
        'CRM field handles to hydrate. [] = no Record fields. "*" = all model fields.',
      ),
    return_format: z
      .union([
        z.literal('id'),
        z.null(),
        z.literal('full'),
        z.literal('meta'),
        z.literal('ids'),
      ])
      .optional()
      .default(null)
      .describe(
        '"id" = string[] response. null = object[] response. Legacy full/meta/ids are shimmed automatically.',
      ),
    include: z
      .array(z.string())
      .optional()
      .describe('Fields to include in response'),
    count: z
      .enum(['exact', 'estimate', 'none'])
      .optional()
      .describe(
        'Total-count strategy. "exact" (default) counts the full match set. "estimate" is a capped count. "none" skips counting (pagination.total is null; hasMore still accurate). Defaults to "none" for return_format "id".',
      ),
    after: z
      .string()
      .optional()
      .describe(
        'Keyset cursor: return instances with id greater than this value in ascending id order (bypasses OFFSET). Ignored when a custom sort is provided.',
      ),
  })
  .refine(
    (data) =>
      data.modelId !== undefined ||
      data['model-id'] !== undefined ||
      data.modelHandle !== undefined ||
      data['model-handle'] !== undefined,
    {
      message:
        'Either modelId/model-id or modelHandle/model-handle is required',
    },
  )

// -----------------------
// instance.create
// -----------------------

const InstanceCreateInputsSchema = requireModelIdentifier(
  ModelIdentifierBaseSchema.extend({
    payload: PayloadOrDataSchema.optional(),
    data: PayloadOrDataSchema.optional(),
    staging: z.boolean().optional(),
    importId: z.string().optional(),
  }),
)

// -----------------------
// instance.upsert
// -----------------------

const InstanceUpsertInputsSchema = requireModelIdentifier(
  ModelIdentifierBaseSchema.extend({
    filters: StructuredFilterSchema,
    data: z.union([z.record(z.string(), z.unknown()), z.string()]).refine(
      (value) => {
        if (typeof value === 'string') {
          try {
            JSON.parse(value)
            return true
          } catch {
            return false
          }
        }
        return true
      },
      { message: 'Invalid JSON data' },
    ),
    staging: z.boolean().optional(),
    importId: z.string().optional(),
  }),
)

// -----------------------
// instance.update
// -----------------------

const InstanceUpdateInputsSchema = z
  .object({
    instanceId: z.string(),
    payload: PayloadOrDataSchema.optional(),
    data: PayloadOrDataSchema.optional(),
    staging: z.boolean().optional(),
    importId: z.string().optional(),
  })
  .refine((value) => value.payload !== undefined || value.data !== undefined, {
    message: 'Either payload or data is required',
    path: ['payload'],
  })

// -----------------------
// Command Schemas
// -----------------------

export const InstanceFindCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.find'),
  }),
  inputs: InstanceFindInputsSchema,
})

export type InstanceFindCommand = z.infer<typeof InstanceFindCommandSchema>

export const InstanceListCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.list'),
  }),
  inputs: InstanceListInputsSchema,
})

export type InstanceListCommand = z.infer<typeof InstanceListCommandSchema>

const InstanceCountInputsSchema = z
  .object({
    modelId: z
      .string()
      .optional()
      .describe('The ID of the model to count instances from'),
    'model-id': z
      .string()
      .optional()
      .describe('The ID of the model to count instances from'),
    modelHandle: z
      .string()
      .optional()
      .describe('The handle of the model to count instances from'),
    'model-handle': z
      .string()
      .optional()
      .describe('The handle of the model to count instances from'),
    filters: StructuredExpressionSchema.optional().describe(
      'Structured filter expression',
    ),
  })
  .refine(
    (data) =>
      data.modelId !== undefined ||
      data['model-id'] !== undefined ||
      data.modelHandle !== undefined ||
      data['model-handle'] !== undefined,
    {
      message:
        'Either modelId/model-id or modelHandle/model-handle is required',
    },
  )

export const InstanceCountCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.count'),
  }),
  inputs: InstanceCountInputsSchema,
})

export type InstanceCountCommand = z.infer<typeof InstanceCountCommandSchema>

export const InstanceCreateCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.create'),
  }),
  inputs: InstanceCreateInputsSchema,
})

export type InstanceCreateCommand = z.infer<typeof InstanceCreateCommandSchema>

export const InstanceUpdateCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.update'),
  }),
  inputs: InstanceUpdateInputsSchema,
})

export type InstanceUpdateCommand = z.infer<typeof InstanceUpdateCommandSchema>

export const InstanceUpsertCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.upsert'),
  }),
  inputs: InstanceUpsertInputsSchema,
})

export type InstanceUpsertCommand = z.infer<typeof InstanceUpsertCommandSchema>

// -----------------------
// instance.upsertMany
// -----------------------

const UpsertManyDataSchema = z
  .union([
    z.array(z.union([z.record(z.string(), z.unknown()), z.string()])),
    z.string(),
  ])
  .describe(
    'Array of instance data to upsert, or a liquid template resolving to a JSON array',
  )

const InstanceUpsertManyInputsSchema = requireModelIdentifier(
  ModelIdentifierBaseSchema.extend({
    matchField: z.string().describe('The field to match existing instances by'),
    matchFields: z
      .union([
        z.array(z.string()),
        z.string(),
      ])
      .optional()
      .describe(
        'Ordered CRM field handles for match fallback; first non-blank value per item wins',
      ),
    data: UpsertManyDataSchema,
    errorMode: z
      .enum(['atomic', 'partial'])
      .optional()
      .default('partial')
      .describe(
        'Error handling: atomic (all-or-nothing) or partial (continue on error)',
      ),
    staging: z.boolean().optional(),
    importId: z.string().optional(),
  }),
)

export const InstanceUpsertManyCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/crm'),
    cmd: z.literal('instance.upsertMany'),
  }),
  inputs: InstanceUpsertManyInputsSchema,
})

export type InstanceUpsertManyCommand = z.infer<
  typeof InstanceUpsertManyCommandSchema
>
